import { debugStore } from '@stores/Debug';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import {
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { throttle } from 'throttle-debounce';
import { computeMasonryLayout } from './layout';
import styles from './VirtualMasonry.module.scss';

const RankOverlay = observer(({ rank }: { rank: number }) => {
  if (!debugStore.showGridRankDebug) return null;
  return <div className={styles.rankOverlay}>{rank}</div>;
});

export interface VirtualMasonryCellProps {
  isVisible: boolean;
  columnIndex: number;
  rowIndex: number;
  width: number;
  height: number;
}

export interface VirtualMasonryRef {
  scrollToTop: () => void;
  getVisibleCells: () => number[];
  scrollToIndex: (index: number, behavior?: ScrollBehavior) => void;
}

interface VirtualMasonryProps {
  width: number;
  minCellWidth?: number;
  masonryCellGap?: number;
  sidesMargin?: number;
  addBorder?: boolean;
  /** Off lets a card's hover lift and shadow show past its cell. */
  clipCells?: boolean;
  onVisibleCellsChange?: (visibleCellIndices: number[]) => void;
  cells: {
    key?: string;
    cardShape: 'tall' | 'short';
    isSelected?: boolean;
    isHighlighted?: boolean;
    children: ReactNode | ((props: VirtualMasonryCellProps) => ReactNode);
  }[];
  overscanWeight?: number;
  throttleTime?: number;
  paddingBottom?: number;
  className?: string;
  /** Optionally specify an external scroll container (e.g., when the parent handles scrolling).
   *  If provided, VirtualMasonry will listen to the scroll events on this container instead of its own internal one. */
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  /** Center cells horizontally by splitting leftover space evenly. Default false (left-aligned). */
  centerCells?: boolean;
  /**
   * Optional content rendered as a sibling of the masonry layout,
   * inside the same scroll container, after all cells. Useful for
   * footnotes / banners that should scroll with the results but
   * aren't part of the virtual-cell layout algorithm.
   */
  footer?: ReactNode;
}

export const VirtualMasonry = forwardRef<VirtualMasonryRef, VirtualMasonryProps>(
  function VirtualMasonry(
    {
      width: desiredWidth,
      minCellWidth = 290,
      masonryCellGap = 10,
      sidesMargin = 0,
      addBorder = true,
      clipCells = true,
      onVisibleCellsChange,
      cells,
      overscanWeight = 1.0,
      throttleTime = 100,
      paddingBottom = 12,
      className,
      scrollContainerRef,
      centerCells = false,
      footer,
    },
    ref,
  ) {
    // Debounce desiredWidth so the layout only recomputes after
    // resizing settles (~150ms), not on every frame during a drag-resize.
    const [debouncedWidth, setDebouncedWidth] = useState(desiredWidth);
    const prevWidthRef = useRef(desiredWidth);

    useEffect(() => {
      const prevWidth = prevWidthRef.current;
      prevWidthRef.current = desiredWidth;
      // Apply immediately when going from 0 to a real width (initial measurement)
      if (prevWidth === 0 && desiredWidth > 0) {
        setDebouncedWidth(desiredWidth);
        return;
      }
      const timer = setTimeout(() => setDebouncedWidth(desiredWidth), 150);
      return () => clearTimeout(timer);
    }, [desiredWidth]);

    // Calculate cell positions and total height.
    // Math lives in ./layout.ts so it can be unit-tested without React.
    const {
      cellPositions,
      totalHeight,
      columnWidth: layoutColumnWidth,
      usedWidth,
    } = useMemo(() => {
      const filteredCells = cells.filter((cell) => cell);
      return computeMasonryLayout({
        cardShapes: filteredCells.map((c) => c.cardShape),
        width: debouncedWidth,
        minCellWidth,
        masonryCellGap,
        centerCells,
      });
    }, [cells, debouncedWidth, minCellWidth, masonryCellGap, centerCells]);

    // Calculate visible cells based on viewport
    const [visibleCells, setVisibleCells] = useState<number[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    // Helper to get whichever scroll container we should use
    const getScrollContainer = useCallback(() => {
      return scrollContainerRef?.current ?? containerRef.current;
    }, [scrollContainerRef]);

    const updateVisibleCells = throttle(throttleTime, () => {
      const container = getScrollContainer();
      if (container) {
        const scrollTop = container.scrollTop;
        const viewportHeight = container.clientHeight;
        const viewportBottom = scrollTop + viewportHeight;
        const overscanHeight = viewportHeight * overscanWeight;

        // Find cells that intersect with the viewport plus overscan
        const visible = cellPositions
          .map((pos, index) => ({ index, pos }))
          .filter(({ pos }) => {
            const cellTop = pos.y;
            const cellBottom = pos.y + pos.height;
            return (
              cellBottom >= scrollTop - overscanHeight && cellTop <= viewportBottom + overscanHeight
            );
          })
          .map(({ index }) => index);

        setVisibleCells(visible);
        onVisibleCellsChange?.(visible);
      }
    });

    useEffect(() => {
      updateVisibleCells();
      const container = getScrollContainer();
      if (container) {
        container.addEventListener('scroll', updateVisibleCells);
      }
      window.addEventListener('resize', updateVisibleCells);
      return () => {
        if (container) {
          container.removeEventListener('scroll', updateVisibleCells);
        }
        window.removeEventListener('resize', updateVisibleCells);
      };
    }, [cellPositions, getScrollContainer]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToTop: () => {
          const container = getScrollContainer();
          container?.scrollTo({ top: 0, behavior: 'instant' });
        },
        getVisibleCells: () => visibleCells,
        scrollToIndex: (index: number, behavior: ScrollBehavior = 'smooth') => {
          const container = getScrollContainer();
          if (!container || index < 0 || index >= cellPositions.length) return;

          const position = cellPositions[index];
          if (!position) return;

          // Calculate scroll position to center the cell in the viewport
          const viewportHeight = container.clientHeight;
          const targetScrollTop = position.y - viewportHeight / 2 + position.height / 2;

          // Ensure scroll position is within bounds
          const maxScrollTop = totalHeight - viewportHeight;
          const clampedScrollTop = Math.max(0, Math.min(targetScrollTop, maxScrollTop));

          container.scrollTo({ top: clampedScrollTop, behavior });
        },
      }),
      [cellPositions, totalHeight, getScrollContainer],
    );

    return (
      <div
        ref={scrollContainerRef ? undefined : containerRef}
        style={
          {
            '--sides-margin': `${sidesMargin}px`,
            '--padding-bottom': `${paddingBottom}px`,
            '--used-width': `${usedWidth}px`,
          } as React.CSSProperties
        }
        className={classNames(styles.container, className, {
          [styles.hasScrollContainer]: !!scrollContainerRef,
        })}
      >
        <div
          style={
            {
              '--total-height': `${totalHeight}px`,
            } as React.CSSProperties
          }
          className={styles.masonryLayout}
        >
          {visibleCells.map((cellIndex) => {
            const cell = cells[cellIndex];
            const pos = cellPositions[cellIndex];
            if (!cell) return null;
            if (!pos) return null;

            const columnIndex = Math.floor(pos.x / (layoutColumnWidth + masonryCellGap));
            const rowIndex = Math.floor(pos.y / (pos.height + masonryCellGap));

            return (
              <div
                key={cell.key || `cell-${cellIndex}`}
                data-highlighted={cell.isHighlighted ? 'true' : 'false'}
                style={
                  {
                    '--cell-left': `${pos.x}px`,
                    '--cell-top': `${pos.y}px`,
                    '--cell-width': `${pos.width}px`,
                    '--cell-height': `${pos.height}px`,
                  } as React.CSSProperties
                }
                className={classNames(styles.cell, {
                  [styles.hasBorder]: addBorder,
                  [styles.selected]: addBorder && cell.isSelected,
                  [styles.unclipped]: !clipCells,
                })}
              >
                {typeof cell.children === 'function'
                  ? cell.children({
                      isVisible: true,
                      columnIndex,
                      rowIndex,
                      width: pos.width,
                      height: pos.height,
                    })
                  : cell.children}
                <RankOverlay rank={cellIndex} />
              </div>
            );
          })}
        </div>
        {footer}
      </div>
    );
  },
);
